import { useRef, useEffect, useState, useCallback, useMemo } from 'react';

interface VirtualListProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  itemHeight: number;
  overscan?: number;
  className?: string;
  onEndReached?: () => void;
  endReachedThreshold?: number;
}

export function VirtualList<T>({
  items,
  renderItem,
  itemHeight,
  overscan = 5,
  className,
  onEndReached,
  endReachedThreshold = 200,
}: VirtualListProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const rafRef = useRef<number | null>(null);
  const lastScrollTime = useRef(0);

  // Measure container height
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(containerRef.current);
    setContainerHeight(containerRef.current.clientHeight);

    return () => resizeObserver.disconnect();
  }, []);

  // Calculate visible range
  const { virtualItems, totalHeight, startIndex, endIndex } = useMemo(() => {
    const totalHeight = items.length * itemHeight;
    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
    const visibleCount = Math.ceil(containerHeight / itemHeight) + overscan * 2;
    const endIndex = Math.min(items.length, startIndex + visibleCount);

    const virtualItems = [];
    for (let i = startIndex; i < endIndex; i++) {
      const item = items[i];
      if (!item) continue;
      virtualItems.push({
        item,
        index: i,
        style: {
          position: 'absolute' as const,
          top: i * itemHeight,
          height: itemHeight,
          left: 0,
          right: 0,
        },
      });
    }

    return { virtualItems, totalHeight, startIndex, endIndex };
  }, [items, scrollTop, containerHeight, itemHeight, overscan]);

  // Handle scroll with RAF throttling for performance
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;

    const now = performance.now();
    
    // Throttle to every 16ms (60fps) maximum
    if (now - lastScrollTime.current < 16) {
      if (rafRef.current === null) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          handleScroll();
        });
      }
      return;
    }

    lastScrollTime.current = now;
    setScrollTop(containerRef.current.scrollTop);

    // Check if near end for infinite scroll
    if (onEndReached) {
      const scrollBottom = containerRef.current.scrollTop + containerRef.current.clientHeight;
      const distanceToEnd = totalHeight - scrollBottom;
      
      if (distanceToEnd < endReachedThreshold) {
        onEndReached();
      }
    }
  }, [totalHeight, onEndReached, endReachedThreshold]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [handleScroll]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!containerRef.current) return;

    const scrollAmount = itemHeight * 10;
    
    switch (e.key) {
      case 'PageDown':
        e.preventDefault();
        containerRef.current.scrollBy({ top: scrollAmount, behavior: 'smooth' });
        break;
      case 'PageUp':
        e.preventDefault();
        containerRef.current.scrollBy({ top: -scrollAmount, behavior: 'smooth' });
        break;
      case 'Home':
        e.preventDefault();
        containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        break;
      case 'End':
        e.preventDefault();
        containerRef.current.scrollTo({ top: totalHeight, behavior: 'smooth' });
        break;
    }
  }, [itemHeight, totalHeight]);

  return (
    <div
      ref={containerRef}
      className={`virtual-list ${className || ''}`}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="listbox"
      aria-label="Results list"
    >
      <div
        className="virtual-list-content"
        style={{ height: totalHeight, position: 'relative' }}
      >
        {virtualItems.map(({ item, index, style }) => (
          <div key={index} style={style} role="option">
            {renderItem(item, index)}
          </div>
        ))}
      </div>
    </div>
  );
}

// Grouped virtual list for explorer view
interface GroupedVirtualListProps<T> {
  groups: {
    id: string;
    title: string;
    items: T[];
  }[];
  renderItem: (item: T, index: number) => React.ReactNode;
  renderGroupHeader: (group: { id: string; title: string; items: T[] }, isCollapsed: boolean) => React.ReactNode;
  itemHeight: number;
  headerHeight: number;
  expandedGroups: Set<string>;
  className?: string;
}

export function GroupedVirtualList<T>({
  groups,
  renderItem,
  renderGroupHeader,
  itemHeight,
  headerHeight,
  expandedGroups,
  className,
}: GroupedVirtualListProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(containerRef.current);
    setContainerHeight(containerRef.current.clientHeight);

    return () => resizeObserver.disconnect();
  }, []);

  // Calculate positions for all items including headers
  const { virtualItems, totalHeight } = useMemo(() => {
    let currentPosition = 0;
    const items: {
      type: 'header' | 'item';
      data: unknown;
      index: number;
      style: React.CSSProperties;
    }[] = [];

    for (const group of groups) {
      const isExpanded = expandedGroups.has(group.id);
      const groupItemCount = isExpanded ? group.items.length : 0;
      const groupHeight = headerHeight + groupItemCount * itemHeight;

      // Add header
      items.push({
        type: 'header',
        data: group,
        index: items.length,
        style: {
          position: 'absolute',
          top: currentPosition,
          height: headerHeight,
          left: 0,
          right: 0,
        },
      });
      currentPosition += headerHeight;

      // Add items if expanded
      if (isExpanded) {
        for (let i = 0; i < group.items.length; i++) {
          items.push({
            type: 'item',
            data: { item: group.items[i], groupIndex: i },
            index: items.length,
            style: {
              position: 'absolute',
              top: currentPosition,
              height: itemHeight,
              left: 0,
              right: 0,
            },
          });
          currentPosition += itemHeight;
        }
      }
    }

    // Filter to visible range
    const visibleStart = Math.max(0, scrollTop - itemHeight);
    const visibleEnd = scrollTop + containerHeight + itemHeight;
    
    const virtualItems = items.filter(
      (item) => {
        const top = item.style.top as number;
        const height = item.style.height as number;
        return top + height >= visibleStart && top <= visibleEnd;
      }
    );

    return { virtualItems, totalHeight: currentPosition };
  }, [groups, expandedGroups, itemHeight, headerHeight, scrollTop, containerHeight]);

  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;
    setScrollTop(containerRef.current.scrollTop);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <div ref={containerRef} className={`virtual-list ${className || ''}`} tabIndex={0}>
      <div style={{ height: totalHeight, position: 'relative' }}>
        {virtualItems.map((item) => (
          <div key={item.index} style={item.style}>
            {item.type === 'header'
              ? renderGroupHeader(item.data as { id: string; title: string; items: T[] }, 
                  expandedGroups.has((item.data as { id: string }).id))
              : renderItem((item.data as { item: T }).item, (item.data as { groupIndex: number }).groupIndex)
            }
          </div>
        ))}
      </div>
    </div>
  );
}
